import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { curveMonotoneX } from '@visx/curve';
import { ComposedChart } from '../../components/charts/composed-chart';
import { LineChart, Line } from '../../components/charts/line-chart';
import { SeriesBar } from '../../components/charts/series-bar';
import { Grid } from '../../components/charts/grid';
import { XAxis } from '../../components/charts/x-axis';
import { YAxis } from '../../components/charts/y-axis';
import { ChartTooltip } from '../../components/charts/tooltip';
import { RefreshCw, ChevronDown, Calendar, Sparkles } from 'lucide-react';
import './MobileDashboard.css';
import { MobileKpiTile } from './MobileKpiTile';
import { MobileSwipeableCharts } from './MobileSwipeableCharts';
import { MobileSummaryTable } from './MobileSummaryTable';
import DashboardDailyBriefing from '../DashboardDailyBriefing/DashboardDailyBriefing';

const CHART_MARGIN = { top: 8, right: 8, bottom: 28, left: 44 };

// HTML legend strip (replaces the recharts <Legend/>).
const MobileChartLegend = ({ items }) => (
  <div className="mobile-chart-legend">
    {items.map((item) => (
      <span key={item.label} className="mobile-chart-legend-item">
        <span className="mobile-chart-legend-swatch" style={{ background: item.color }} />
        {item.label}
      </span>
    ))}
  </div>
);

export const MobileDashboard = ({
  dashboardData,
  calculatedMetrics,
  comparisonMetrics,
  isLoadingComparison,
  financialData,
  financialMetrics,
  currentOrganization,
  lastUpdated,
  dateRange,
  selectedQuickDate,
  selectedLocations,
  availableLocations,
  locationNames,
  onQuickDateChange,
  onLocationChange,
  onRefresh,
  formatCurrency,
  formatCurrencyRounded,
  formatPercentage,
  calculatePercentChange,
  calculateAbsoluteDelta,
  formatAbsoluteDelta,
  formatComparisonValue,
  chartData,
  financialChartData,
  selectedFinancialQuickDate,
  onFinancialQuickDateChange,
  revenueTableData,
  operationsTableData,
  financialTableData,
  aiAssistantEnabled,
  briefingHotelId,
  briefingRefreshKey,
  briefingComparisonMode,
  briefingComparisonLabel,
  comparisonOptions = [],
  selectedComparisonMode,
  onComparisonModeChange,
  isComparisonOptionDisabled,
  comparisonSummaryText,
  onSaveComparisonDefault,
  hasUnsavedComparisonPreference,
  isSavingComparisonPreference,
  comparisonSaveSuccess,
  comparisonTableLabel,
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showLocationModal, setShowLocationModal] = useState(false);
  // Briefing detail is opt-in on a phone: a small header button expands it.
  const [showBriefing, setShowBriefing] = useState(false);

  // Update current time every minute for staleness indicator
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Staleness indicator logic (matching mobile app)
  const getStalenessIndicator = useCallback(() => {
    if (!lastUpdated) {
      return {
        color: '#64748b',
        backgroundColor: '#f1f5f9',
        dotColor: '#94a3b8',
        isStale: false,
      };
    }

    const now = currentTime;
    const minutesSinceUpdate = Math.floor((now - lastUpdated) / (1000 * 60));
    const stalenessRatio = Math.min(minutesSinceUpdate / 60, 1);

    // Interpolate between green and red over 60 minutes
    const r = Math.round(34 + (239 - 34) * stalenessRatio);
    const g = Math.round(197 + (68 - 197) * stalenessRatio);
    const b = Math.round(94 + (56 - 94) * stalenessRatio);

    const backgroundColor = `rgba(${r}, ${g}, ${b}, 0.1)`;
    const color = `rgb(${r}, ${g}, ${b})`;
    const dotColor = `rgb(${r}, ${g}, ${b})`;

    return {
      color,
      backgroundColor,
      dotColor,
      isStale: stalenessRatio > 0.5,
    };
  }, [lastUpdated, currentTime]);

  const staleness = getStalenessIndicator();

  const quickDateOptions = [
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'last7', label: 'Last 7 days' },
    { key: 'last30', label: 'Last 30 days' },
    { key: 'last60', label: 'Last 60 days' },
    { key: 'last90', label: 'Last 90 days' },
    { key: 'thisMonth', label: 'This month' },
    { key: 'lastMonth', label: 'Last month' },
  ];

  const getSelectedLocationText = () => {
    if (selectedLocations.includes('all')) {
      return 'All Locations';
    }
    if (selectedLocations.length === 0) {
      return 'Select Locations';
    }
    if (selectedLocations.length === 1) {
      const locationId = selectedLocations[0];
      return locationNames[parseInt(locationId)] || `Location ${locationId}`;
    }
    return `${selectedLocations.length} Locations Selected`;
  };

  const handleLocationToggle = (locationId) => {
    if (locationId === 'all') {
      if (selectedLocations.includes('all')) {
        onLocationChange([]);
      } else {
        onLocationChange(['all']);
      }
    } else {
      const locationIdStr = locationId.toString();
      const currentSelection = selectedLocations.filter(id => id !== 'all');

      if (currentSelection.includes(locationIdStr)) {
        const updated = currentSelection.filter(id => id !== locationIdStr);
        onLocationChange(updated);
      } else {
        const updated = [...currentSelection, locationIdStr];
        if (updated.length === availableLocations.length) {
          onLocationChange(['all']);
        } else {
          onLocationChange(updated);
        }
      }
    }
  };

  const isLocationSelected = (locationId) => {
    if (locationId === 'all') {
      return selectedLocations.includes('all');
    }
    return selectedLocations.includes(locationId.toString());
  };

  // bklit time-series shells coerce x values through new Date(); dashboard rows
  // carry display strings ("Aug 5" / "Aug 2026"), so synthesize evenly spaced
  // dates for spacing and surface the real labels via xLabels + tooltip title.
  const laborChartRows = useMemo(
    () => (chartData || []).map((d, i) => ({ ...d, chartX: new Date(Date.UTC(2024, 0, 1 + i)) })),
    [chartData]
  );
  const laborChartLabels = useMemo(() => (chartData || []).map((d) => d.date), [chartData]);
  const financialChartRows = useMemo(
    () => (financialChartData || []).map((d, i) => ({ ...d, chartX: new Date(Date.UTC(2024, 0, 1 + i)) })),
    [financialChartData]
  );
  const financialChartLabels = useMemo(() => (financialChartData || []).map((d) => d.date), [financialChartData]);

  // Prepare revenue charts data using chartData (prepared by Dashboard parent)
  const revenueCharts = dashboardData && chartData && chartData.length > 0 ? [
    {
      type: 'line',
      title: 'Revenue Trend',
      data: chartData,
      yAxisType: 'currency',
    },
    {
      type: 'bar',
      title: 'Occupancy Trend',
      data: chartData,
      yAxisType: 'percentage',
    },
  ] : [];

  const renderMobileMetricWithBenchmark = (value, row, metricKey, formatter) => {
    const benchmark = row.comparison?.[metricKey];
    const hasBenchmark = benchmark !== null && benchmark !== undefined;

    return (
      <div style={{ textAlign: 'right' }}>
        <div>{formatter(value)}</div>
        {hasBenchmark && (
          <div style={{ marginTop: 2, color: '#8a94a0', fontSize: 11, whiteSpace: 'nowrap' }}>
            {comparisonTableLabel || 'Benchmark'}: {formatter(benchmark)}
          </div>
        )}
      </div>
    );
  };

  // Revenue summary table columns
  const revenueColumns = [
    { key: 'locationName', label: 'Location' },
    {
      key: 'occupiedRooms',
      label: 'Occ Rooms',
      render: (val, row) => renderMobileMetricWithBenchmark(val, row, 'occupiedRooms', metric => metric ? metric.toLocaleString() : '0'),
    },
    {
      key: 'compRooms',
      label: 'Comp',
      render: (val, row) => renderMobileMetricWithBenchmark(val, row, 'compRooms', metric => metric ? metric.toLocaleString() : '0'),
    },
    {
      key: 'occupancyRate',
      label: 'Occ %',
      render: (val, row) => renderMobileMetricWithBenchmark(val, row, 'occupancyRate', formatPercentage),
    },
    {
      key: 'adr',
      label: 'ADR',
      render: (val, row) => renderMobileMetricWithBenchmark(val, row, 'adr', formatCurrency),
    },
    {
      key: 'revPAR',
      label: 'RevPAR',
      render: (val, row) => renderMobileMetricWithBenchmark(val, row, 'revPAR', formatCurrency),
    },
    {
      key: 'roomRevenue',
      label: 'Room Rev',
      render: (val, row) => renderMobileMetricWithBenchmark(val, row, 'roomRevenue', formatCurrencyRounded),
    },
    {
      key: 'totalRevenue',
      label: 'Total Rev',
      render: (val, row) => renderMobileMetricWithBenchmark(val, row, 'totalRevenue', formatCurrencyRounded),
    },
  ];

  // Operations summary table columns
  const operationsColumns = [
    { key: 'locationName', label: 'Location' },
    {
      key: 'hoursWorked',
      label: 'Hours',
      render: (val) => val?.toLocaleString(),
    },
    {
      key: 'budgetedHours',
      label: 'Budget Hrs',
      render: (val) => val?.toLocaleString(),
    },
    {
      key: 'hpor',
      label: 'HPOR',
      render: (val) => val?.toFixed(2),
    },
    {
      key: 'otHours',
      label: 'OT Hours',
      render: (val) => val?.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    },
    {
      key: 'otPercentage',
      label: 'OT %',
      render: (val) => formatPercentage(val),
    },
    {
      key: 'wages',
      label: 'Wages',
      render: (val) => formatCurrency(val),
    },
    {
      key: 'dpor',
      label: '$/Occ Rm',
      render: (val) => formatCurrency(val),
    },
  ];

  // Financial summary table columns
  const financialColumns = [
    { key: 'locationName', label: 'Location' },
    {
      key: 'revenue',
      label: 'Revenue',
      render: (val) => formatCurrency(val),
    },
    {
      key: 'budgetRevenue',
      label: 'Bgt Rev',
      render: (val) => formatCurrency(val),
    },
    {
      key: 'revenueVariance',
      label: 'Rev Var',
      render: (val) => {
        const isFav = val >= 0;
        return (
          <span style={{ color: isFav ? '#0b8f5e' : '#d84343' }}>
            {formatCurrency(Math.abs(val))} {isFav ? '\u2713' : '\u2717'}
          </span>
        );
      },
    },
    {
      key: 'expense',
      label: 'Expenses',
      render: (val) => formatCurrency(val),
    },
    {
      key: 'budgetExpense',
      label: 'Bgt Exp',
      render: (val) => formatCurrency(val),
    },
    {
      key: 'expenseVariance',
      label: 'Exp Var',
      render: (val) => {
        const isFav = val >= 0;
        return (
          <span style={{ color: isFav ? '#0b8f5e' : '#d84343' }}>
            {formatCurrency(Math.abs(val))} {isFav ? '\u2713' : '\u2717'}
          </span>
        );
      },
    },
    {
      key: 'netIncome',
      label: 'Net Inc',
      render: (val) => formatCurrency(val),
    },
    {
      key: 'budgetNetIncome',
      label: 'Bgt Net Inc',
      render: (val) => formatCurrency(val),
    },
    {
      key: 'netIncomeVariance',
      label: 'NI Var',
      render: (val) => {
        const isFav = val >= 0;
        return (
          <span style={{ color: isFav ? '#0b8f5e' : '#d84343', fontWeight: 600 }}>
            {formatCurrency(Math.abs(val))} {isFav ? '\u2713' : '\u2717'}
          </span>
        );
      },
    },
  ];

  return (
    <div className="mobile-dashboard">
      {/* Mobile Header */}
      <div className="mobile-header">
        <div className="mobile-header-title">
          <div className="mobile-title-row">
            <h1>Dashboard</h1>
            {aiAssistantEnabled && (
              <button
                type="button"
                className={`mobile-briefing-btn${showBriefing ? ' on' : ''}`}
                onClick={() => setShowBriefing((v) => !v)}
                aria-label="AI daily briefing"
              >
                <Sparkles size={15} />
                Briefing
              </button>
            )}
          </div>
          <p className="mobile-subtitle">
            Performance metrics for {currentOrganization?.name || 'your organization'}
          </p>
        </div>

        {/* Horizontal Scrolling Quick Date Buttons */}
        <div className="mobile-quick-date-section">
          <div className="mobile-quick-date-scroll">
            {quickDateOptions.map((option) => {
              const isActive = selectedQuickDate === option.key;
              return (
                <button
                  key={option.key}
                  className={`mobile-quick-date-button ${isActive ? 'active' : ''}`}
                  onClick={() => onQuickDateChange(option.key)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        {comparisonOptions.length > 0 && (
          <div className="mobile-compare-section">
            <div className="mobile-compare-label">Compare to</div>
            <div className="mobile-compare-scroll">
              {comparisonOptions.map((option) => {
                const isActive = selectedComparisonMode === option.key;
                const isDisabled = isComparisonOptionDisabled?.(option);
                return (
                  <button
                    key={option.key}
                    type="button"
                    disabled={isDisabled}
                    className={`mobile-compare-button ${isActive ? 'active' : ''}`}
                    onClick={() => {
                      if (!isDisabled) onComparisonModeChange?.(option.key);
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            <div className="mobile-compare-status">
              <span>{comparisonSummaryText}</span>
              <button
                type="button"
                onClick={onSaveComparisonDefault}
                disabled={!hasUnsavedComparisonPreference || isSavingComparisonPreference}
              >
                {comparisonSaveSuccess ? 'Saved' : isSavingComparisonPreference ? 'Saving...' : 'Set default'}
              </button>
            </div>
          </div>
        )}

        {/* Controls Row */}
        <div className="mobile-controls-row">
          <button
            className="mobile-control-button"
            onClick={() => setShowLocationModal(true)}
          >
            <span className="mobile-control-text">{getSelectedLocationText()}</span>
            <ChevronDown size={16} />
          </button>

          <button className="mobile-control-button small">
            <Calendar size={16} />
            <span className="mobile-control-text-small">Custom</span>
          </button>

          <button className="mobile-refresh-button" onClick={onRefresh}>
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="mobile-content">

        {/* Revenue Performance Section */}
        <div className="mobile-section">
          <div className="mobile-section-header">
            <h2>Revenue Performance</h2>
            <p>Key revenue metrics and trends</p>
          </div>

          {/* Revenue KPI Tiles - 2 column grid */}
          <div className="mobile-kpi-grid">
            <MobileKpiTile
              label="RevPAR"
              value={formatCurrency(calculatedMetrics?.aggregated?.revPAR || 0)}
              sub={`${calculatedMetrics?.aggregated?.totalAvailableRooms || 0} available rooms`}
              accentColor="#f97316"
              comparison={isLoadingComparison ? undefined : comparisonMetrics ? {
                value: comparisonMetrics.aggregated.revPAR,
                formattedValue: formatComparisonValue(comparisonMetrics.aggregated.revPAR, 'currency'),
                percentChange: calculatePercentChange(
                  calculatedMetrics?.aggregated?.revPAR || 0,
                  comparisonMetrics.aggregated.revPAR
                ),
                absoluteDelta: formatAbsoluteDelta(
                  calculateAbsoluteDelta(
                    calculatedMetrics?.aggregated?.revPAR || 0,
                    comparisonMetrics.aggregated.revPAR
                  ),
                  'currency'
                ),
                label: comparisonMetrics.label,
                dateRange: comparisonMetrics.dateRange
              } : null}
            />

            <MobileKpiTile
              label="ADR"
              value={formatCurrency(calculatedMetrics?.aggregated?.adr || 0)}
              sub={`${calculatedMetrics?.aggregated?.totalOccupiedRooms || 0} occupied rooms`}
              accentColor="#fb923c"
              comparison={isLoadingComparison ? undefined : comparisonMetrics ? {
                value: comparisonMetrics.aggregated.adr,
                formattedValue: formatComparisonValue(comparisonMetrics.aggregated.adr, 'currency'),
                percentChange: calculatePercentChange(
                  calculatedMetrics?.aggregated?.adr || 0,
                  comparisonMetrics.aggregated.adr
                ),
                absoluteDelta: formatAbsoluteDelta(
                  calculateAbsoluteDelta(
                    calculatedMetrics?.aggregated?.adr || 0,
                    comparisonMetrics.aggregated.adr
                  ),
                  'currency'
                ),
                label: comparisonMetrics.label,
                dateRange: comparisonMetrics.dateRange
              } : null}
            />

            <MobileKpiTile
              label="Occupancy"
              value={`${(calculatedMetrics?.aggregated?.occupancyRate || 0).toFixed(1)}%`}
              sub={`${calculatedMetrics?.aggregated?.totalOccupiedRooms || 0} of ${calculatedMetrics?.aggregated?.totalAvailableRooms || 0} rooms`}
              accentColor="#38bdf8"
              comparison={isLoadingComparison ? undefined : comparisonMetrics ? {
                value: comparisonMetrics.aggregated.occupancyRate,
                formattedValue: formatComparisonValue(comparisonMetrics.aggregated.occupancyRate, 'percentage'),
                percentChange: calculatePercentChange(
                  calculatedMetrics?.aggregated?.occupancyRate || 0,
                  comparisonMetrics.aggregated.occupancyRate
                ),
                absoluteDelta: formatAbsoluteDelta(
                  calculateAbsoluteDelta(
                    calculatedMetrics?.aggregated?.occupancyRate || 0,
                    comparisonMetrics.aggregated.occupancyRate
                  ),
                  'percentage'
                ),
                label: comparisonMetrics.label,
                dateRange: comparisonMetrics.dateRange
              } : null}
            />

            <MobileKpiTile
              label="Total Revenue"
              value={formatCurrency(calculatedMetrics?.aggregated?.totalRevenue || 0)}
              sub={`Room: ${formatCurrency(calculatedMetrics?.aggregated?.totalRoomRevenue || 0)}`}
              accentColor="#fdba74"
              comparison={isLoadingComparison ? undefined : comparisonMetrics ? {
                value: comparisonMetrics.aggregated.totalRevenue,
                formattedValue: formatComparisonValue(comparisonMetrics.aggregated.totalRevenue, 'currency'),
                percentChange: calculatePercentChange(
                  calculatedMetrics?.aggregated?.totalRevenue || 0,
                  comparisonMetrics.aggregated.totalRevenue
                ),
                absoluteDelta: formatAbsoluteDelta(
                  calculateAbsoluteDelta(
                    calculatedMetrics?.aggregated?.totalRevenue || 0,
                    comparisonMetrics.aggregated.totalRevenue
                  ),
                  'currency'
                ),
                label: comparisonMetrics.label,
                dateRange: comparisonMetrics.dateRange
              } : null}
            />
          </div>

          {/* Swipeable Charts */}
          {revenueCharts.length > 0 && (
            <MobileSwipeableCharts
              charts={revenueCharts}
              formatCurrency={formatCurrency}
              formatPercentage={formatPercentage}
            />
          )}

          {/* Revenue Performance Summary Table */}
          <MobileSummaryTable
            title="Performance Summary"
            columns={revenueColumns}
            data={revenueTableData}
            accentColor="#f97316"
          />
        </div>

        {/* Operations Metrics Section */}
        <div className="mobile-section">
          <div className="mobile-section-header">
            <h2>Operations Metrics</h2>
            <p>Labor efficiency and productivity</p>
          </div>

          {/* Operations KPI Tiles - 2 column grid */}
          <div className="mobile-kpi-grid">
            <MobileKpiTile
              label="Hours Per Occ Room"
              value={calculatedMetrics?.aggregated?.hoursPerOccupiedRoom?.toFixed(2) || '0'}
              sub={`${calculatedMetrics?.aggregated?.totalHoursWorked?.toFixed(0) || 0} total hours`}
              accentColor="#f97316"
              comparison={isLoadingComparison ? undefined : comparisonMetrics ? {
                value: comparisonMetrics.aggregated.hoursPerOccupiedRoom,
                formattedValue: formatComparisonValue(comparisonMetrics.aggregated.hoursPerOccupiedRoom, 'number'),
                percentChange: calculatePercentChange(
                  calculatedMetrics?.aggregated?.hoursPerOccupiedRoom || 0,
                  comparisonMetrics.aggregated.hoursPerOccupiedRoom
                ),
                absoluteDelta: formatAbsoluteDelta(
                  calculateAbsoluteDelta(
                    calculatedMetrics?.aggregated?.hoursPerOccupiedRoom || 0,
                    comparisonMetrics.aggregated.hoursPerOccupiedRoom
                  ),
                  'number'
                ),
                label: comparisonMetrics.label,
                dateRange: comparisonMetrics.dateRange
              } : null}
            />

            <MobileKpiTile
              label="$ Per Occ Room"
              value={formatCurrency(calculatedMetrics?.aggregated?.dollarPerOccupiedRoom || 0)}
              sub={`${formatCurrency(calculatedMetrics?.aggregated?.totalWages || 0)} total wages`}
              accentColor="#fb923c"
              comparison={isLoadingComparison ? undefined : comparisonMetrics ? {
                value: comparisonMetrics.aggregated.dollarPerOccupiedRoom,
                formattedValue: formatComparisonValue(comparisonMetrics.aggregated.dollarPerOccupiedRoom, 'currency'),
                percentChange: calculatePercentChange(
                  calculatedMetrics?.aggregated?.dollarPerOccupiedRoom || 0,
                  comparisonMetrics.aggregated.dollarPerOccupiedRoom
                ),
                absoluteDelta: formatAbsoluteDelta(
                  calculateAbsoluteDelta(
                    calculatedMetrics?.aggregated?.dollarPerOccupiedRoom || 0,
                    comparisonMetrics.aggregated.dollarPerOccupiedRoom
                  ),
                  'currency'
                ),
                label: comparisonMetrics.label,
                dateRange: comparisonMetrics.dateRange
              } : null}
            />

            <MobileKpiTile
              label="OT Percentage"
              value={formatPercentage(calculatedMetrics?.aggregated?.otPercentage || 0)}
              sub={`${calculatedMetrics?.aggregated?.totalOTHours?.toFixed(1) || 0} OT hours`}
              accentColor="#fbbf24"
              comparison={isLoadingComparison ? undefined : comparisonMetrics ? {
                value: comparisonMetrics.aggregated.otPercentage,
                formattedValue: formatComparisonValue(comparisonMetrics.aggregated.otPercentage, 'percentage'),
                percentChange: calculatePercentChange(
                  calculatedMetrics?.aggregated?.otPercentage || 0,
                  comparisonMetrics.aggregated.otPercentage
                ),
                absoluteDelta: formatAbsoluteDelta(
                  calculateAbsoluteDelta(
                    calculatedMetrics?.aggregated?.otPercentage || 0,
                    comparisonMetrics.aggregated.otPercentage
                  ),
                  'percentage'
                ),
                label: comparisonMetrics.label,
                dateRange: comparisonMetrics.dateRange
              } : null}
            />
          </div>

          {/* Labor Hours Chart */}
          {chartData && chartData.length > 0 && (
            <div className="mobile-chart-card-standalone">
              <div className="mobile-chart-header">
                <h3>Labor Hours Analysis</h3>
              </div>
              <div className="mobile-chart-content">
                <ComposedChart
                  data={laborChartRows}
                  xDataKey="chartX"
                  xLabels={laborChartLabels}
                  aspectRatio={null}
                  style={{ height: 220 }}
                  margin={CHART_MARGIN}
                >
                  <Grid horizontal />
                  <XAxis />
                  <YAxis />
                  <SeriesBar dataKey="hoursWorked" fill="#ee7623" radius={3} />
                  <Line
                    dataKey="budgetedHours"
                    stroke="#3a3a44"
                    strokeWidth={2}
                    curve={curveMonotoneX}
                  />
                  <ChartTooltip
                    title={null}
                    rows={(point) => [
                      { color: '#ee7623', label: 'Hours Worked', value: `${(point.hoursWorked ?? 0).toFixed(2)} hours` },
                      { color: '#3a3a44', label: 'Budgeted Hours', value: `${(point.budgetedHours ?? 0).toFixed(2)} hours` },
                    ]}
                  />
                </ComposedChart>
                <MobileChartLegend
                  items={[
                    { label: 'Hours Worked', color: '#ee7623' },
                    { label: 'Budgeted Hours', color: '#3a3a44' },
                  ]}
                />
              </div>
            </div>
          )}

          {/* Operations Performance Summary Table */}
          <MobileSummaryTable
            title="Operations Summary"
            columns={operationsColumns}
            data={operationsTableData}
            accentColor="#fb923c"
          />
        </div>

        {/* Financial Performance Section */}
        <div className="mobile-section">
          <div className="mobile-section-header">
            <h2>Financial Performance</h2>
            <p>Budget vs actuals</p>
          </div>

          {/* Financial Quick Date Filters */}
          <div className="mobile-quick-date-scroll">
            {[
              { key: 'lastMonth', label: 'Last Month' },
              { key: 'last6', label: 'Last 6 Months' },
              { key: 'last12', label: 'Last 12 Months' }
            ].map((option) => {
              const isActive = selectedFinancialQuickDate === option.key;
              return (
                <button
                  key={option.key}
                  className={`mobile-quick-date-button financial ${isActive ? 'active' : ''}`}
                  onClick={() => onFinancialQuickDateChange(option.key)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          {/* Financial KPI Tiles - 2 column grid */}
          <div className="mobile-kpi-grid">
            <MobileKpiTile
              label="Total Revenue"
              value={formatCurrency(financialMetrics?.totalRevenue || 0)}
              sub={`Budget: ${formatCurrency(financialMetrics?.totalBudgetRevenue || 0)}`}
              accentColor="#0b8f5e"
              comparison={null}
            />

            <MobileKpiTile
              label="Total Expenses"
              value={formatCurrency(financialMetrics?.totalExpense || 0)}
              sub={`Budget: ${formatCurrency(financialMetrics?.totalBudgetExpense || 0)}`}
              accentColor="#d84343"
              comparison={null}
            />

            <MobileKpiTile
              label="Net Income"
              value={formatCurrency(financialMetrics?.totalNetIncome || 0)}
              sub={`Budget: ${formatCurrency(financialMetrics?.totalBudgetNetIncome || 0)}`}
              accentColor={financialMetrics?.totalNetIncome >= 0 ? "#0b8f5e" : "#d84343"}
              comparison={null}
            />
          </div>

          {/* Revenue vs Expenses Chart */}
          {financialChartData && financialChartData.length > 0 && (
            <div className="mobile-chart-card-standalone">
              <div className="mobile-chart-header">
                <h3>Revenue vs Expenses</h3>
              </div>
              <div className="mobile-chart-content">
                <LineChart
                  data={financialChartRows}
                  xDataKey="chartX"
                  xLabels={financialChartLabels}
                  aspectRatio={null}
                  style={{ height: 220 }}
                  margin={CHART_MARGIN}
                >
                  <Grid horizontal />
                  <XAxis />
                  <YAxis
                    formatValue={(value) => {
                      if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
                      if (value >= 1000) return `$${Math.round(value / 1000)}k`;
                      return `$${Math.round(value)}`;
                    }}
                  />
                  <Line
                    dataKey="revenue"
                    stroke="#ee7623"
                    strokeWidth={2}
                    curve={curveMonotoneX}
                  />
                  <Line
                    dataKey="expense"
                    stroke="#3a3a44"
                    strokeWidth={2}
                    curve={curveMonotoneX}
                  />
                  <Line
                    dataKey="netIncome"
                    stroke="#0b8f5e"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    curve={curveMonotoneX}
                  />
                  <ChartTooltip
                    title={null}
                    rows={(point) => [
                      { color: '#ee7623', label: 'Revenue', value: formatCurrency(point.revenue) },
                      { color: '#3a3a44', label: 'Expenses', value: formatCurrency(point.expense) },
                      { color: '#0b8f5e', label: 'Net Income', value: formatCurrency(point.netIncome) },
                    ]}
                  />
                </LineChart>
                <MobileChartLegend
                  items={[
                    { label: 'Revenue', color: '#ee7623' },
                    { label: 'Expenses', color: '#3a3a44' },
                    { label: 'Net Income', color: '#0b8f5e' },
                  ]}
                />
              </div>
            </div>
          )}

          {/* Financial Performance Summary Table */}
          <MobileSummaryTable
            title="Financial Summary"
            columns={financialColumns}
            data={financialTableData}
            accentColor="#0b8f5e"
          />
        </div>
      </div>

      {/* AI Daily Briefing — full-screen sheet opened from the header button;
          the detail never sits inline on the dashboard. */}
      {aiAssistantEnabled && showBriefing && (
        <div className="mobile-briefing-overlay" onClick={() => setShowBriefing(false)}>
          <div className="mobile-briefing-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-briefing-sheet-head">
              <h3>AI Daily Briefing</h3>
              <button type="button" onClick={() => setShowBriefing(false)} aria-label="Close briefing">×</button>
            </div>
            <div className="mobile-briefing-sheet-body">
              <DashboardDailyBriefing
                hotelId={briefingHotelId || 'all'}
                hotelName={currentOrganization?.name || 'Portfolio'}
                refreshKey={briefingRefreshKey}
                comparisonMode={briefingComparisonMode}
                comparisonLabel={briefingComparisonLabel}
              />
            </div>
          </div>
        </div>
      )}

      {/* Location Modal */}
      {showLocationModal && (
        <div className="mobile-modal-overlay" onClick={() => setShowLocationModal(false)}>
          <div className="mobile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-modal-header">
              <h3>Select Locations</h3>
              <button onClick={() => setShowLocationModal(false)}>×</button>
            </div>
            <div className="mobile-modal-body">
              <button
                className={`mobile-location-option ${isLocationSelected('all') ? 'selected' : ''}`}
                onClick={() => handleLocationToggle('all')}
              >
                <span>All Locations</span>
                {isLocationSelected('all') && <span className="mobile-checkmark">✓</span>}
              </button>
              {availableLocations.map((locationId) => (
                <button
                  key={locationId}
                  className={`mobile-location-option ${isLocationSelected(locationId) ? 'selected' : ''}`}
                  onClick={() => handleLocationToggle(locationId)}
                >
                  <span>{locationNames[locationId] || `Location ${locationId}`}</span>
                  {isLocationSelected(locationId) && <span className="mobile-checkmark">✓</span>}
                </button>
              ))}
            </div>
            <div className="mobile-modal-footer">
              <button
                className="mobile-modal-button"
                onClick={() => setShowLocationModal(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
